import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { contactApi, contactQueryKeys } from "service/crm/api/contact";
import { dealApi, dealQueryKeys } from "service/crm/api/deal";
import { NonAssociatedDealParams } from "types/crm/deal";

interface UseQueryOptions {
  enabled?: boolean;
}

/** Contacts associated with a deal — GET /crm/deals/{dealUuid}/contacts */
export const useDealContacts = (dealUuid: string) => {
  return useQuery({
    queryKey: dealQueryKeys.contacts(dealUuid),
    queryFn: () => dealApi.getDealContacts(dealUuid),
    enabled: !!dealUuid,
  });
};

export const useNonAssociatedDealContacts = (
  { dealUuid, search, limit }: Omit<NonAssociatedDealParams, "page">,
  options: UseQueryOptions = {},
) => {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: contactQueryKeys.nonAssociatedByDeal(dealUuid, {
      search,
      limit,
    }),
    queryFn: ({ pageParam }) =>
      contactApi.getNonAssociatedDealContacts({
        dealUuid,
        search,
        limit,
        page: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.last ? undefined : lastPage.page + 1,
    enabled: (options.enabled ?? true) && !!dealUuid,
  });

  return {
    contacts: data?.pages.flatMap((p) => p.content) ?? [],
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    totalElements: data?.pages[0]?.totalElements ?? 0,
  };
};
